import { AlertTriangle, CircleHelp, Clock, Lock, RefreshCw, UserRound } from 'lucide-react'

import { Button, CheckboxField, ChoiceChips, DayStrip, TreeNav, Workbench } from '@krp/ui-kit'

import '../../../shared/uc1b/journals.css'
import { QueryState } from '../../../shared/uc1b/QueryState'
import type { ShiftState, ShiftStateFilter, ShiftsActions, ShiftsVm } from '../model/types'

const STATE_ICON: Readonly<Record<ShiftState, typeof Clock>> = { open: Clock, closed: Lock, unknown: CircleHelp }

/**
 * Раздел «Смены» кабинета AQSI (решение владельца 26.10.2026): смены, как их хранит кабинет, — по кассам за период. Слева — «Где
 * смотрим» (кабинет → магазин → касса), справа — месяц стрелками и дни, состояние, кассир, «Только мои» и сами смены. Чек здесь
 * не открывается: «Чеки этой смены» ведёт во вкладку «Чеки» с отбором по кассе и смене. Состояние смены — значок и слово, не
 * только цвет. Экран получает vm и actions и больше ничего (R02).
 */
export function ShiftsScreen({ vm, actions }: { vm: ShiftsVm; actions: ShiftsActions }) {
  const nav = vm.nav
  return (
    <section className="aqsi-journal" aria-labelledby="aqsi-shifts-title">
      <header className="aqsi-journal__head">
        <h2 id="aqsi-shifts-title">Смены · {vm.title}</h2>
        <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} busy={vm.query.refreshing} onClick={actions.reload}>
          Обновить
        </Button>
      </header>

      <Workbench
        sideTitle="Где смотрим"
        sideSummary={nav.summaryText}
        side={
          nav.query.status === 'ready' ? (
            <TreeNav label="Где смотрим: магазины и кассы" nodes={nav.nodes} onSelect={actions.nodeChosen} onToggle={actions.nodeToggled} />
          ) : (
            <QueryState query={nav.query} emptyText="Кассы кабинета не найдены" onRetry={actions.reload} />
          )
        }
      >
        <DayStrip pager today={vm.today} value={vm.range} onChange={actions.periodChanged} onMonth={actions.monthShown} label="Период смен" />
        {!vm.wholeMonth && (
          <Button variant="ghost" onClick={() => actions.monthShown(vm.range.from)}>
            Весь месяц
          </Button>
        )}
        <ChoiceChips label="Состояние смены" items={vm.states} value={vm.state} onChange={(id) => actions.stateChanged(id as ShiftStateFilter)} />
        {vm.cashiers.length > 1 && <ChoiceChips label="Кассир" items={vm.cashiers} value={vm.cashier} onChange={actions.cashierChanged} />}
        <CheckboxField label="Только мои смены (открывал, принимал или вёл я)" checked={vm.mineOnly} onChange={actions.mineOnlyChanged} />

        {vm.warnings.map((warning) => (
          <p key={warning} className="aqsi-journal__note" data-tone="warning" role="status">
            <AlertTriangle size={16} aria-hidden="true" />
            <span>{warning}</span>
          </p>
        ))}
        {vm.mineOnly && vm.mineQuery.error !== null && (
          <p className="aqsi-journal__note" data-tone="warning" role="status">
            <AlertTriangle size={16} aria-hidden="true" />
            <span>Мои смены не прочитаны: {vm.mineQuery.error.message}</span>
          </p>
        )}

        <p role="status">
          <strong>{vm.countText}</strong>
        </p>
        <QueryState query={vm.query} emptyText="За период смен нет" onRetry={actions.reload} />
        {vm.rows.length > 0 && (
          <ul className="aqsi-journal__list" aria-label="Смены">
            {vm.rows.map((row) => {
              const Icon = STATE_ICON[row.state]
              return (
                <li key={row.key} className="aqsi-journal__item" data-open={row.state === 'open'} data-state={row.state}>
                  <div className="aqsi-journal__item-main">
                    <Icon size={16} aria-hidden="true" />
                    <strong>{row.numberText}</strong>
                    <span>{row.registerText}</span>
                    <span>{row.stateText}</span>
                    {row.mine && (
                      <span>
                        <UserRound size={14} aria-hidden="true" /> моя смена
                      </span>
                    )}
                    <Button
                      variant="ghost"
                      onClick={() => actions.openReceipts(row.registerId, row.number)}
                      aria-label={`Чеки этой смены: ${row.numberText}, ${row.registerText}`}
                    >
                      Чеки этой смены
                    </Button>
                  </div>
                  <div className="aqsi-journal__meta">
                    <span>{row.openedText}</span>
                    {row.closedText !== null && <span>{row.closedText}</span>}
                    <span>Кассиры: {row.cashiersText}</span>
                    <span>{row.totalsText}</span>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </Workbench>
    </section>
  )
}
